import { Controller } from "stimulus"
import { formatCurrencyAmount } from "../utils/format_currency_amount"

// Splitting a donation across multiple charities with live impact feedback and an option to increase the donation amount.
export default class extends Controller {
  static targets = [
    "pickerCard", "splitsContainer", "emptyHint", "hiddenFields",
    "impactCalculator", "boostSliderRow", "boostSlider", "boostSliderValue",
    "boostSliderMinLabel", "boostSliderMaxLabel"
  ]

  static values = {
    charities: Array,
    // Charities already added on load (e.g. OFTW's deep link to a single Top Pick).
    initialEins: Array,
    // { ein: percentage } to start those charities at, instead of an equal split -- an existing
    // pledge's own mix when it's edited from "Manage your pledge".
    initialSplits: Object
  }

  connect() {
    this.addedEins = []
    this.splits = {}
    this.splitLocks = {}
    this.boostBaseline = null
    this.element.addEventListener("donation-wizard:pageShown", this.handlePageShown.bind(this))
    // donation_flow_shared_controller.js fires this on every amount edit/currency switch -- without
    // it, boosting on Allocate then going back and typing a *larger* amount leaves the slider's
    // floor at the old (smaller) baseline, letting the donor drag back below what they just entered.
    this.formRoot?.addEventListener("donation-flow-shared:amountChanged", () => this.resetBoostBaseline())
    if (this.hasInitialEinsValue) this.initialEinsValue.forEach((ein) => this.addCharity(ein))
    this.applyInitialSplits()
  }

  // Only a complete split (every added charity, totalling 100) replaces the equal one.
  applyInitialSplits() {
    if (!this.hasInitialSplitsValue) return
    const percentages = this.addedEins.map((ein) => Number(this.initialSplitsValue[ein]))
    if (percentages.some((pct) => !Number.isInteger(pct) || pct < 0)) return
    if (percentages.reduce((sum, pct) => sum + pct, 0) !== 100) return

    this.addedEins.forEach((ein, index) => { this.splits[ein] = percentages[index] })
    this.renderAll()
  }

  handlePageShown(event) {
    if (event.detail.pageId !== "allocate") return
    this.renderAll()
  }

  // When the donor edits the Amount step's own input/presets, new amount always resets the boost floor
  resetBoostBaseline() {
    this.boostBaseline = this.amountCents
    this.renderAll()
  }

  charityFor(ein) {
    return this.charitiesValue.find((c) => c.ein === ein)
  }

  get formRoot() {
    return this.element.closest("form")
  }

  // amount_cents lives in the Amount step's own fieldset, a sibling of this controller's element
  // (the Allocate step's fieldset) — not a descendant, so it has to be looked up from the form root.
  get amountCents() {
    const input = this.formRoot?.querySelector('[name="donation_form[amount_cents]"]')
    return parseInt(input?.value, 10) || 0
  }

  setAmountCents(cents) {
    const dollarsInput = this.formRoot?.querySelector('[data-donation-flow-shared-target="amountDollars"]')
    const centsInput = this.formRoot?.querySelector('[name="donation_form[amount_cents]"]')
    if (dollarsInput) dollarsInput.value = String(cents / (this.isZeroDecimalCurrency ? 1 : 100))
    if (centsInput) centsInput.value = String(cents)
  }

  get currencySymbolTarget() {
    return this.formRoot?.querySelector('[data-donation-flow-shared-target="amountCurrencySymbol"]')
  }

  get currencySymbol() {
    return this.currencySymbolTarget?.textContent || ""
  }

  // Stripe's zero-decimal currencies (JPY, KRW, VND, ...) have no minor unit
  get isZeroDecimalCurrency() {
    return this.currencySymbolTarget?.dataset.zeroDecimal === "true"
  }

  // ── Picker ────────────────────────────────────────────────────────────
  toggleCharity(event) {
    const { charityEin } = event.currentTarget.dataset
    if (this.addedEins.includes(charityEin)) {
      this.removeCharity(charityEin)
    } else {
      this.addCharity(charityEin)
    }
  }

  addCharity(ein) {
    if (this.addedEins.includes(ein)) return
    const charity = this.charityFor(ein)
    if (!charity) return

    this.addedEins.push(ein)
    this.splits[ein] = 0
    this.splitLocks[ein] = false
    this.equalizeAddedSplits()
    this.syncPickerCardUI(ein)
    this.renderAll()
  }

  removeCharity(ein) {
    if (!this.addedEins.includes(ein)) return

    this.addedEins = this.addedEins.filter((id) => id !== ein)
    delete this.splits[ein]
    delete this.splitLocks[ein]
    this.equalizeAddedSplits()
    this.syncPickerCardUI(ein)
    this.renderAll()
  }

  syncPickerCardUI(ein) {
    const card = this.pickerCardTargets.find((el) => el.dataset.charityEin === ein)
    const btn = card?.querySelector('[data-charity-ein="' + ein + '"].donation-flow__charity-add-btn')
    if (!btn) return
    const added = this.addedEins.includes(ein)
    card.classList.toggle("selected", added)
    btn.classList.toggle("added", added)
    btn.setAttribute("aria-pressed", String(added))
  }

  distributeIntegerAmount(ids, weights, total) {
    if (ids.length === 0) return

    const weightSum = weights.reduce((sum, w) => sum + w, 0)
    const raw = weightSum > 0 ? weights.map((w) => (w / weightSum) * total) : weights.map(() => total / ids.length)
    const floors = raw.map(Math.floor)
    const flooredTotal = floors.reduce((sum, f) => sum + f, 0)
    const toDistribute = Math.min(Math.max(0, total - flooredTotal), ids.length)

    const remainderOrder = raw
      .map((r, index) => ({ index, remainder: r - floors[index] }))
      .sort((a, b) => b.remainder - a.remainder)

    const shares = floors.slice()
    remainderOrder.slice(0, toDistribute).forEach(({ index }) => { shares[index] += 1 })

    ids.forEach((id, index) => { this.splits[id] = shares[index] })
  }

  equalizeAddedSplits() {
    if (this.addedEins.length === 0) return
    this.addedEins.forEach((ein) => { this.splitLocks[ein] = false })
    this.distributeIntegerAmount(this.addedEins, this.addedEins.map(() => 1), 100)
  }

  // Sliders 
  redistributeSplits(draggedEin, draggedVal) {
    const lockedIds = this.addedEins.filter((id) => id !== draggedEin && this.splitLocks[id])
    const otherIds = this.addedEins.filter((id) => id !== draggedEin && !this.splitLocks[id])

    const lockedTotal = lockedIds.reduce((sum, id) => sum + this.splits[id], 0)
    const maxForDragged = Math.max(0, 100 - lockedTotal)
    const val = Math.min(draggedVal, maxForDragged)
    this.splits[draggedEin] = val

    const remaining = Math.max(0, 100 - val - lockedTotal)

    if (otherIds.length === 1) {
      this.splits[otherIds[0]] = remaining
    } else if (otherIds.length > 1) {
      const otherTotal = otherIds.reduce((sum, id) => sum + this.splits[id], 0)
      const weights = otherTotal > 0 ? otherIds.map((id) => this.splits[id] / otherTotal) : otherIds.map(() => 1)
      this.distributeIntegerAmount(otherIds, weights, remaining)
    }

    this.renderAll()
  }

  sliderInput(event) {
    const ein = event.currentTarget.dataset.charityEin
    this.redistributeSplits(ein, Number(event.currentTarget.value))
  }

  sliderPointerDown(event) {
    const ein = event.currentTarget.dataset.charityEin
    // Captured once, before any unlock below, so the drag's eventual auto-lock decision
    this.dragStartUnlockedCount = this.addedEins.filter((id) => !this.splitLocks[id]).length
    // Grabbing a locked slider unlocks it so the drag can redistribute normally.
    if (this.splitLocks[ein]) this.setLock(ein, false)
  }

  sliderPointerUp(event) {
    const ein = event.currentTarget.dataset.charityEin
    // Lock the dragged slider once set, unless it's one of the last two unlocked ones (need at least two free sliders for redistribution to have somewhere to go).
    if (this.dragStartUnlockedCount > 2) this.setLock(ein, true)
  }

  toggleLock(event) {
    const ein = event.currentTarget.dataset.lockEin
    this.setLock(ein, !this.splitLocks[ein])
  }

  setLock(ein, locked) {
    this.splitLocks[ein] = locked
    const btn = this.splitsContainerTarget.querySelector(`[data-lock-ein="${ein}"]`)
    if (btn) {
      btn.classList.toggle("locked", locked)
      btn.setAttribute("aria-pressed", String(locked))
      btn.textContent = locked ? "Unlock" : "Lock"
    }
  }

  // ── "Boost your impact" slider ──────────────────────────────────────────
  // Floor is wherever the amount stood when this step was shown (or last raised to); dragging
  // can only move the real amount up from there, never back below it.
  refreshBoostSlider() {
    if (!this.hasBoostSliderRowTarget) return

    const amountCents = this.amountCents
    if (this.addedEins.length === 0 || amountCents <= 0) {
      this.boostSliderRowTarget.classList.add("is-hidden")
      return
    }
    this.boostSliderRowTarget.classList.remove("is-hidden")

    if (this.boostBaseline === null || amountCents < this.boostBaseline) this.boostBaseline = amountCents

    const baseline = this.boostBaseline
    const max = Math.max(baseline * 3, baseline + 100)
    this.boostSliderTarget.min = String(baseline)
    this.boostSliderTarget.max = String(max)
    this.boostSliderTarget.value = String(amountCents)
    this.boostSliderValueTarget.textContent = formatCurrencyAmount(amountCents, this.currencySymbol, this.isZeroDecimalCurrency)
    this.boostSliderMinLabelTarget.textContent = formatCurrencyAmount(baseline, this.currencySymbol, this.isZeroDecimalCurrency)
    this.boostSliderMaxLabelTarget.textContent = formatCurrencyAmount(max, this.currencySymbol, this.isZeroDecimalCurrency)
  }

  boostSliderInput(event) {
    const min = Number(event.currentTarget.min)
    const amountCents = Math.max(Number(event.currentTarget.value), min)
    event.currentTarget.value = String(amountCents)
    this.setAmountCents(amountCents)
    this.renderAll()
  }

  // Rounds each charity's raw share to an integer percentage that always sums to exactly 100.
  // Rounding each share independently (Math.round per charity) can under/overshoot the total.
  roundedSplits() {
    if (this.addedEins.length === 0) return {}

    const entries = this.addedEins.map((ein) => {
      const floor = Math.floor(this.splits[ein])
      return { ein, floor, remainder: this.splits[ein] - floor }
    })
    const flooredTotal = entries.reduce((sum, e) => sum + e.floor, 0)
    const toDistribute = Math.min(Math.max(0, 100 - flooredTotal), entries.length)

    const result = {}
    entries.forEach(({ ein, floor }) => { result[ein] = floor })
    entries
      .slice()
      .sort((a, b) => b.remainder - a.remainder)
      .slice(0, toDistribute)
      .forEach(({ ein }) => { result[ein] += 1 })

    return result
  }

  // ── Rendering
  renderAll() {
    this.renderSliders()
    this.renderHiddenFields()
    this.renderImpact()
    this.refreshBoostSlider()
    this.emptyHintTarget.classList.toggle("is-hidden", this.addedEins.length > 0)
    // For a client controller that saves particular splits differently (OFTW's custom mix).
    this.element.dispatchEvent(new CustomEvent("charity-split:changed", { detail: { splits: this.roundedSplits() }, bubbles: true }))
  }

  renderSliders() {
    const existingEins = Array.from(this.splitsContainerTarget.children).map((el) => el.dataset.charityEin)

    // Remove rows for charities no longer added.
    existingEins.forEach((ein) => {
      if (!this.addedEins.includes(ein)) {
        this.splitsContainerTarget.querySelector(`[data-charity-ein="${ein}"]`)?.remove()
      }
    })

    // Add rows for newly-added charities.
    this.addedEins.forEach((ein) => {
      if (existingEins.includes(ein)) return
      const charity = this.charityFor(ein)
      const row = document.createElement("div")
      row.className = "donation-flow__charity-slider-row"
      row.dataset.charityEin = ein

      const nameSpan = document.createElement("span")
      nameSpan.className = "donation-flow__charity-name"
      nameSpan.textContent = charity.name

      const range = document.createElement("input")
      range.type = "range"
      range.className = "donation-flow__charity-range"
      range.min = "0"
      range.max = "100"
      range.step = "1"
      range.value = "0"
      range.dataset.charityEin = ein
      range.setAttribute("aria-label", `${charity.name} percentage`)

      const pctLabel = document.createElement("span")
      pctLabel.className = "donation-flow__charity-pct-label"
      pctLabel.textContent = "0%"

      const lockBtn = document.createElement("button")
      lockBtn.type = "button"
      lockBtn.className = "donation-flow__charity-lock-btn"
      lockBtn.dataset.lockEin = ein
      lockBtn.setAttribute("aria-pressed", "false")
      lockBtn.textContent = "Lock"

      row.append(nameSpan, range, pctLabel, lockBtn)
      this.splitsContainerTarget.appendChild(row)

      range.addEventListener("input", this.sliderInput.bind(this))
      range.addEventListener("pointerdown", this.sliderPointerDown.bind(this))
      range.addEventListener("pointerup", this.sliderPointerUp.bind(this))
      lockBtn.addEventListener("click", this.toggleLock.bind(this))
    })

    // Sync every visible row's value/label to current state.
    const rounded = this.roundedSplits()
    this.addedEins.forEach((ein) => {
      const row = this.splitsContainerTarget.querySelector(`[data-charity-ein="${ein}"]`)
      if (!row) return
      const val = rounded[ein]
      row.querySelector("input[type=range]").value = String(val)
      row.querySelector(".donation-flow__charity-pct-label").textContent = `${val}%`
    })
  }

  renderHiddenFields() {
    const rounded = this.roundedSplits()
    this.hiddenFieldsTarget.replaceChildren()
    this.addedEins.forEach((ein, index) => {
      const einInput = document.createElement("input")
      einInput.type = "hidden"
      einInput.name = `donation_form[custom_allocations][${index}][organization_ein]`
      einInput.value = ein

      const pctInput = document.createElement("input")
      pctInput.type = "hidden"
      pctInput.name = `donation_form[custom_allocations][${index}][percentage]`
      pctInput.value = String(rounded[ein])

      this.hiddenFieldsTarget.append(einInput, pctInput)
    })
  }

  get splitFractions() {
    const total = this.addedEins.reduce((sum, ein) => sum + this.splits[ein], 0)
    return this.addedEins.map((ein) => ({
      ein,
      charity: this.charityFor(ein),
      fraction: total > 0 ? this.splits[ein] / total : 0
    }))
  }

  renderImpact() {
    if (!this.hasImpactCalculatorTarget) return

    const impactController = this.application.getControllerForElementAndIdentifier(this.impactCalculatorTarget, "wellby-impact-calculator")
    if (!impactController) return

    const allocations = this.splitFractions.map(({ charity, fraction }) => ({
      name: charity.name,
      share_percent: fraction * 100,
      cost_per_wellby: charity.cost_per_wellby,
      outcome_label: charity.outcome_label,
      cost_per_outcome: charity.cost_per_outcome
    }))

    impactController.configValue = { ...impactController.configValue, allocations }
    impactController.amountCentsValue = this.amountCents
    impactController.boostBaselineCentsValue = this.boostBaseline || 0
  }
}
